import React, { useState } from 'react';
import { addDoc, collection } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useToast } from '../../contexts/ToastContext';
import '../../styles/ReportViewer.css';

const ReportViewer = ({ reportData, onSaveReport, currentUser, statusConfig, districts }) => {
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  const [viewMode, setViewMode] = useState('table'); // table, cards, summary
  const [exportFormat, setExportFormat] = useState('csv');

  const handleSaveReport = async () => {
    if (!reportData.config.name) {
      toast.warning('Please provide a name for the report before saving.');
      return;
    }

    setSaving(true);
    try {
      await addDoc(collection(db, 'savedReports'), {
        name: reportData.config.name,
        description: reportData.config.description,
        config: reportData.config,
        createdBy: currentUser.uid,
        createdAt: new Date(),
        isDefault: false
      });

      toast.success('Report saved successfully!');
      onSaveReport();
    } catch (error) {
      console.error('Error saving report:', error);
      toast.error('Error saving report. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleExport = () => {
    switch (exportFormat) {
      case 'csv':
        exportToCSV();
        break;
      case 'json':
        exportToJSON();
        break;
      case 'pdf':
        exportToPDF();
        break;
      default:
        exportToCSV();
    }
  };

  const exportToCSV = () => {
    const data = Array.isArray(reportData.data) ? reportData.data : Object.values(reportData.data).flat();

    if (data.length === 0) {
      toast.warning('No data to export.');
      return;
    }

    const headers = reportData.config.columns.map(col => getFieldLabel(col));
    const csvContent = [
      headers.join(','),
      ...data.map(row => 
        reportData.config.columns.map(col => {
          const value = getNestedValue(row, col);
          return `"${value || ''}"`;
        }).join(',')
      )
    ].join('\n');

    downloadFile(csvContent, `${reportData.config.name || 'report'}.csv`, 'text/csv');
  };

  const exportToJSON = () => {
    const jsonContent = JSON.stringify({
      reportName: reportData.config.name,
      generatedAt: reportData.generatedAt,
      config: reportData.config,
      data: reportData.data
    }, null, 2);

    downloadFile(jsonContent, `${reportData.config.name || 'report'}.json`, 'application/json');
  };

  const exportToPDF = () => {
    // For PDF export, we'll create a printable HTML version
    const printWindow = window.open('', '_blank');
    const htmlContent = generatePrintableHTML();
    
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.print();
  };

  const generatePrintableHTML = () => {
    const data = Array.isArray(reportData.data) ? reportData.data : Object.values(reportData.data).flat();
    
    return `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${reportData.config.name || 'Report'}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            h1 { color: #2c3e50; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f8f9fa; font-weight: bold; }
            .report-meta { margin-bottom: 20px; padding: 15px; background-color: #f8f9fa; border-radius: 5px; }
          </style>
        </head>
        <body>
          <h1>${reportData.config.name || 'Report'}</h1>
          <div class="report-meta">
            <p><strong>Description:</strong> ${reportData.config.description || 'No description'}</p>
            <p><strong>Generated:</strong> ${new Date(reportData.generatedAt).toLocaleString()}</p>
            <p><strong>Total Records:</strong> ${data.length}</p>
          </div>
          <table>
            <thead>
              <tr>
                ${reportData.config.columns.map(col => `<th>${getFieldLabel(col)}</th>`).join('')}
              </tr>
            </thead>
            <tbody>
              ${data.map(row => `
                <tr>
                  ${reportData.config.columns.map(col => `<td>${getNestedValue(row, col) || ''}</td>`).join('')}
                </tr>
              `).join('')}
            </tbody>
          </table>
        </body>
      </html>
    `;
  };

  const downloadFile = (content, filename, mimeType) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const getNestedValue = (obj, path) => {
    const value = path.split('.').reduce((current, key) => {
      if (current && typeof current === 'object') {
        if (current[key] instanceof Date) {
          return current[key].toLocaleDateString();
        }
        if (current[key] && current[key].toDate && typeof current[key].toDate === 'function') {
          return current[key].toDate().toLocaleDateString();
        }
        return current[key];
      }
      return undefined;
    }, obj);

    // Handle objects that might be returned (like vote objects)
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      // If it's a vote object with yes/no/abstain keys, convert to readable format
      if (value.hasOwnProperty('yes') || value.hasOwnProperty('no') || value.hasOwnProperty('abstain')) {
        const votes = [];
        if (value.yes) votes.push(`Yes: ${value.yes}`);
        if (value.no) votes.push(`No: ${value.no}`);
        if (value.abstain) votes.push(`Abstain: ${value.abstain}`);
        return votes.length > 0 ? votes.join(', ') : 'No votes recorded';
      }
      
      // For other objects, try to convert to JSON string or return a readable representation
      try {
        return JSON.stringify(value);
      } catch (e) {
        return '[Object]';
      }
    }

    // Map values to human-readable descriptions
    return mapValueToDescription(path, value);
  };

  const mapValueToDescription = (fieldPath, value) => {
    if (!value) return value;

    // Map conference status
    if (fieldPath === 'conferenceStatus' && statusConfig?.conferenceStatuses) {
      const status = statusConfig.conferenceStatuses.find(s => s.id === value);
      return status ? status.label : value;
    }

    // Map process stage
    if (fieldPath === 'processStage' && statusConfig?.processStages) {
      const stage = statusConfig.processStages.find(s => s.id === value);
      return stage ? stage.label : value;
    }

    // Map district
    if (fieldPath === 'district' && districts) {
      const district = districts.find(d => d.id === value);
      return district ? district.name : value;
    }

    // Map latest report fields
    if (fieldPath === 'latestReport.category' && value) {
      // Map action report categories if needed
      const categoryMap = {
        'psychological': 'Psychological Evaluation',
        'background': 'Background Check',
        'interview': 'Interview Report',
        'recommendation': 'Recommendation',
        'other': 'Other'
      };
      return categoryMap[value] || value;
    }

    if (fieldPath === 'latestReport.status' && value) {
      // Map action report status if needed
      const statusMap = {
        'pending': 'Pending',
        'approved': 'Approved',
        'rejected': 'Rejected',
        'in-review': 'In Review'
      };
      return statusMap[value] || value;
    }

    if (fieldPath === 'latestReport.vote' && value) {
      // Map vote values
      const voteMap = {
        'approve': 'Approve',
        'reject': 'Reject',
        'defer': 'Defer',
        'abstain': 'Abstain'
      };
      return voteMap[value] || value;
    }

    // Map action report fields for direct action report data
    if (fieldPath === 'category' && value) {
      const categoryMap = {
        'psychological': 'Psychological Evaluation',
        'background': 'Background Check',
        'interview': 'Interview Report',
        'recommendation': 'Recommendation',
        'other': 'Other'
      };
      return categoryMap[value] || value;
    }

    if (fieldPath === 'status' && value) {
      const statusMap = {
        'pending': 'Pending',
        'approved': 'Approved',
        'rejected': 'Rejected',
        'in-review': 'In Review'
      };
      return statusMap[value] || value;
    }

    if (fieldPath === 'vote' && value) {
      const voteMap = {
        'approve': 'Approve',
        'reject': 'Reject',
        'defer': 'Defer',
        'abstain': 'Abstain'
      };
      return voteMap[value] || value;
    }

    // Map gender values
    if (fieldPath === 'gender' && value) {
      const genderMap = {
        'male': 'Male',
        'female': 'Female',
        'other': 'Other',
        'prefer-not-to-say': 'Prefer not to say'
      };
      return genderMap[value] || value;
    }

    // Map ethnicity values
    if (fieldPath === 'ethnicity' && value) {
      const ethnicityMap = {
        'african-american': 'African American',
        'asian': 'Asian',
        'caucasian': 'Caucasian',
        'hispanic': 'Hispanic',
        'native-american': 'Native American',
        'pacific-islander': 'Pacific Islander',
        'multiracial': 'Multiracial',
        'other': 'Other',
        'prefer-not-to-say': 'Prefer not to say'
      };
      return ethnicityMap[value] || value;
    }

    return value;
  };

  const getFieldLabel = (fieldKey) => {
    // This should match the field definitions from ReportBuilder
    const fieldLabels = {
      'firstName': 'First Name',
      'lastName': 'Last Name',
      'email': 'Email',
      'conferenceStatus': 'Conference Status',
      'processStage': 'Process Stage',
      'district': 'District',
      'homeChurch': 'Home Church',
      'currentAppointment': 'Current Appointment',
      'mentor': 'Mentor',
      'dateOfBirth': 'Date of Birth',
      'gender': 'Gender',
      'ethnicity': 'Ethnicity',
      'language': 'Primary Language',
      'createdAt': 'Created Date',
      'updatedAt': 'Last Updated',
      'category': 'Category',
      'type': 'Report Type',
      'status': 'Status',
      'title': 'Title',
      'description': 'Description',
      'recommendation': 'Recommendation',
      'vote': 'Vote',
      'submittedBy': 'Submitted By',
      'submittedAt': 'Submitted Date',
      'reviewedBy': 'Reviewed By',
      'reviewedAt': 'Reviewed Date',
      'courseOfStudy.completedCredits': 'Completed Credits',
      'courseOfStudy.totalCredits': 'Total Credits',
      'courseOfStudy.completionPercentage': 'Completion %',
      'totalReports': 'Total Reports',
      'latestReport.category': 'Latest Report Category',
      'latestReport.status': 'Latest Report Status',
      'latestReport.vote': 'Latest Vote',
      'latestReport.submittedAt': 'Latest Report Date'
    };
    
    return fieldLabels[fieldKey] || fieldKey;
  };

  const renderTableView = () => {
    const data = Array.isArray(reportData.data) ? reportData.data : Object.values(reportData.data).flat();
    
    if (data.length === 0) {
      return <div className="no-data">No data found matching the report criteria.</div>;
    }

    return (
      <div className="table-container">
        <table className="report-table">
          <thead>
            <tr>
              {reportData.config.columns.map(column => (
                <th key={column}>{getFieldLabel(column)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, index) => (
              <tr key={index}>
                {reportData.config.columns.map(column => (
                  <td key={column}>
                    {getNestedValue(row, column) || '—'}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderGroupedView = () => {
    if (typeof reportData.data !== 'object' || Array.isArray(reportData.data)) {
      return renderTableView();
    }

    return (
      <div className="grouped-container">
        {Object.entries(reportData.data).map(([groupName, groupData]) => (
          <div key={groupName} className="group-section">
            <h3 className="group-header">
              {groupName} ({groupData.length} items)
            </h3>
            <div className="group-table">
              <table className="report-table">
                <thead>
                  <tr>
                    {reportData.config.columns.map(column => (
                      <th key={column}>{getFieldLabel(column)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {groupData.map((row, index) => (
                    <tr key={index}>
                      {reportData.config.columns.map(column => (
                        <td key={column}>
                          {getNestedValue(row, column) || '—'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderSummaryView = () => {
    const data = Array.isArray(reportData.data) ? reportData.data : Object.values(reportData.data).flat();
    
    const summary = {
      totalRecords: data.length,
      fieldSummaries: {}
    };

    // Generate field summaries
    reportData.config.columns.forEach(column => {
      const values = data.map(row => getNestedValue(row, column)).filter(val => val !== null && val !== undefined && val !== '');
      const uniqueValues = [...new Set(values)];
      
      summary.fieldSummaries[column] = {
        label: getFieldLabel(column),
        totalValues: values.length,
        uniqueValues: uniqueValues.length,
        topValues: uniqueValues.slice(0, 5)
      };
    });

    return (
      <div className="summary-container">
        <div className="summary-overview">
          <h3>Report Summary</h3>
          <div className="summary-stats">
            <div className="stat-item">
              <span className="stat-label">Total Records:</span>
              <span className="stat-value">{summary.totalRecords}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Columns:</span>
              <span className="stat-value">{reportData.config.columns.length}</span>
            </div>
          </div>
        </div>

        <div className="field-summaries">
          <h4>Field Analysis</h4>
          {Object.entries(summary.fieldSummaries).map(([field, fieldSummary]) => (
            <div key={field} className="field-summary">
              <h5>{fieldSummary.label}</h5>
              <div className="field-stats">
                <span>Values: {fieldSummary.totalValues}</span>
                <span>Unique: {fieldSummary.uniqueValues}</span>
              </div>
              {fieldSummary.topValues.length > 0 && (
                <div className="top-values">
                  <strong>Sample values:</strong> {fieldSummary.topValues.join(', ')}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const getTotalRecords = () => {
    return Array.isArray(reportData.data) ? reportData.data.length : Object.values(reportData.data).reduce((sum, group) => sum + group.length, 0);
  };

  return (
    <div className="report-viewer">
      <div className="viewer-header">
        <div className="report-info">
          <h2>{reportData.config.name || 'Untitled Report'}</h2>
          {reportData.config.description && (
            <p className="report-description">{reportData.config.description}</p>
          )}
          <div className="report-meta">
            <span>Generated: {new Date(reportData.generatedAt).toLocaleString()}</span>
            <span>Records: {getTotalRecords()}</span>
          </div>
        </div>

        <div className="viewer-actions">
          <div className="view-controls">
            <label>View:</label>
            <select 
              value={viewMode} 
              onChange={(e) => setViewMode(e.target.value)}
              className="form-select"
            >
              <option value="table">Table</option>
              <option value="summary">Summary</option>
            </select>
          </div>

          <div className="export-controls">
            <select 
              value={exportFormat} 
              onChange={(e) => setExportFormat(e.target.value)}
              className="form-select"
            >
              <option value="csv">CSV</option>
              <option value="json">JSON</option>
              <option value="pdf">PDF</option>
            </select>
            <button 
              className="btn btn-secondary"
              onClick={handleExport}
            >
              Export
            </button>
          </div>

          <button 
            className="btn btn-primary"
            onClick={handleSaveReport}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Report'}
          </button>
        </div>
      </div>

      <div className="viewer-content">
        {viewMode === 'table' && (reportData.config.groupBy ? renderGroupedView() : renderTableView())}
        {viewMode === 'summary' && renderSummaryView()}
      </div>
    </div>
  );
};

export default ReportViewer;
